// frontend/src/pages/ControlRoom/CompletedTasksPanel.jsx
import { useEffect, useMemo, useState } from "react";
import { Trash2, Settings } from "lucide-react";
import axios from "axios";
import { toast } from "@/hooks/use-toast";

const HIDDEN_KEY = "vox.completed.hidden_ids";
const RETENTION_KEY = "vox.completed.retention_days";

function safeJsonParse(v, fallback) {
  try {
    return JSON.parse(v);
  } catch {
    return fallback;
  }
}

function toMsDays(days) {
  const n = Number(days);
  if (!Number.isFinite(n) || n <= 0) return 30 * 24 * 60 * 60 * 1000;
  return n * 24 * 60 * 60 * 1000;
}

function pickCompletedTimestamp(t) {
  // Best effort. Prefer updated_at if present, otherwise created_at, otherwise now.
  const s = t?.updated_at || t?.updatedAt || t?.created_at || t?.createdAt;
  const d = s ? new Date(s) : new Date();
  const ms = d.getTime();
  return Number.isFinite(ms) ? ms : Date.now();
}

export default function CompletedTasksPanel() {
  const [tasks, setTasks] = useState([]);
  const [hiddenIds, setHiddenIds] = useState(() => {
    return safeJsonParse(localStorage.getItem(HIDDEN_KEY) || "[]", []);
  });

  const [retentionDays, setRetentionDays] = useState(() => {
    const v = localStorage.getItem(RETENTION_KEY);
    const n = v ? Number(v) : 30;
    return Number.isFinite(n) && n > 0 ? n : 30;
  });

  const [showSettings, setShowSettings] = useState(false);
  const [draftRetention, setDraftRetention] = useState(retentionDays);

  async function loadCompleted() {
    try {
      const res = await axios.get("/api/tasks");
      const list = Array.isArray(res.data) ? res.data : [];
      setTasks(list);
    } catch {
      toast({
        title: "Could not load completed tasks",
        description: "Vox tried to check the done pile, but the signal fizzled.",
        variant: "destructive",
      });
    }
  }

  useEffect(() => {
    loadCompleted();
  }, []);

  // persist hidden ids and retention
  useEffect(() => {
    try {
      localStorage.setItem(HIDDEN_KEY, JSON.stringify(hiddenIds));
    } catch {}
  }, [hiddenIds]);

  useEffect(() => {
    try {
      localStorage.setItem(RETENTION_KEY, String(retentionDays));
    } catch {}
  }, [retentionDays]);

  const completedVisible = useMemo(() => {
    const now = Date.now();
    const cutoff = now - toMsDays(retentionDays);

    return (tasks || [])
      .filter((t) => (t?.status || "").toLowerCase() === "completed")
      .filter((t) => !hiddenIds.includes(t.id))
      .filter((t) => pickCompletedTimestamp(t) >= cutoff)
      .sort((a, b) => pickCompletedTimestamp(b) - pickCompletedTimestamp(a));
  }, [tasks, hiddenIds, retentionDays]);

  const hideOne = (id) => {
    setHiddenIds((prev) => {
      if (prev.includes(id)) return prev;
      return [...prev, id];
    });

    toast({
      title: "Cleared",
      description: "Poof. Gone from sight. Still safe in the archive.",
    });
  };

  const clearAllVisible = () => {
    const ids = completedVisible.map((t) => t.id).filter(Boolean);
    if (ids.length === 0) {
      toast({ title: "Nothing to clear", description: "The done pile is already clean." });
      return;
    }

    setHiddenIds((prev) => {
      const set = new Set(prev);
      ids.forEach((id) => set.add(id));
      return Array.from(set);
    });

    toast({
      title: "Clear All",
      description: "The done pile has been swept off the stage.",
    });
  };

  const openSettings = () => {
    setDraftRetention(retentionDays);
    setShowSettings(true);
  };

  const saveSettings = () => {
    const n = Number(draftRetention);
    const value = Number.isFinite(n) && n > 0 ? Math.floor(n) : 30;
    setRetentionDays(value);
    setShowSettings(false);

    toast({
      title: "Retention updated",
      description: `Completed tasks will fade after ${value} day${value === 1 ? "" : "s"}.`,
    });
  };

  return (
    <div className="completed-panel">
      <div className="completed-panel-header">
        <div className="completed-panel-title">Completed Tasks</div>

        <div className="completed-panel-actions">
          <button
            type="button"
            className="voxy-icon-btn"
            onClick={openSettings}
            title="Retention settings"
            aria-label="Retention settings"
          >
            <Settings size={18} />
          </button>

          <button
            type="button"
            className="voxy-clear-btn"
            onClick={clearAllVisible}
            title="Clear all completed tasks from the list"
          >
            Clear All
          </button>
        </div>
      </div>

      <div className="completed-panel-meta">
        Auto-clear window: <span className="completed-panel-meta-strong">{retentionDays} days</span>
      </div>

      <div className="completed-panel-list">
        {completedVisible.length === 0 ? (
          <div className="completed-empty">
            <div className="completed-empty-title">No completed tasks here.</div>
            <div className="completed-empty-text">
              When you complete a task, Vox will toast “That’s Done!” and it will appear here.
            </div>
          </div>
        ) : (
          completedVisible.map((t) => (
            <div key={t.id} className="completed-row">
              <div className="completed-row-main">
                <div className="completed-row-title">{t.title}</div>
                <div className="completed-row-sub">
                  Completed on{" "}
                  {new Date(pickCompletedTimestamp(t)).toLocaleDateString(undefined, {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  })}
                </div>
              </div>

              <button
                type="button"
                className="completed-trash"
                onClick={() => hideOne(t.id)}
                title="Remove from this list"
                aria-label="Remove from this list"
              >
                <Trash2 size={18} />
              </button>
            </div>
          ))
        )}
      </div>

      {showSettings && (
        <div className="voxy-modal-overlay" role="dialog" aria-modal="true">
          <div className="voxy-modal">
            <div className="voxy-modal-title">Completed Tasks Retention</div>
            <div className="voxy-modal-text">
              Choose how many days completed tasks stay visible here.
              This never deletes from the database. It only clears the list view.
            </div>

            <div className="voxy-modal-form">
              <label className="voxy-modal-label" htmlFor="retentionDays">
                Days before auto-clear
              </label>
              <input
                id="retentionDays"
                type="number"
                min="1"
                max="365"
                value={draftRetention}
                onChange={(e) => setDraftRetention(e.target.value)}
                className="voxy-modal-input"
              />
            </div>

            <div className="voxy-modal-actions">
              <button
                type="button"
                className="voxy-secondary-btn"
                onClick={() => setShowSettings(false)}
              >
                Cancel
              </button>
              <button type="button" className="voxy-primary-btn" onClick={saveSettings}>
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
